/**
 * MatterHealthBadge - Compact health indicator for matter lists
 *
 * Shows at-a-glance health status:
 * - Healthy (green): Recent activity, no immediate concerns
 * - At Risk (amber): Needs attention - stale or upcoming deadline
 * - Critical (red): Action required - very stale or imminent deadline
 */

import { useMemo } from 'react';
import {
  CheckCircle,
  Warning,
  XCircle,
} from '@phosphor-icons/react';

export type HealthLevel = 'healthy' | 'at_risk' | 'critical';

interface MatterHealthBadgeProps {
  /** ISO timestamp of last activity */
  lastActivityDate?: string | number | null;
  /** ISO date string of next hearing */
  nextHearingDate?: string | null;
  /** Number of overdue tasks (if available) */
  overdueTasks?: number;
  /** Compact mode shows only icon, no text */
  compact?: boolean;
  /** Tooltip/title text */
  showTooltip?: boolean;
}

const HEALTH_CONFIG: Record<HealthLevel, {
  bg: string;
  text: string;
  icon: typeof CheckCircle;
  label: string;
  bgRow: string;
}> = {
  healthy: {
    bg: 'bg-emerald-100 dark:bg-emerald-500/20',
    text: 'text-emerald-700 dark:text-emerald-400',
    icon: CheckCircle,
    label: 'Healthy',
    bgRow: 'border-l-emerald-500',
  },
  at_risk: {
    bg: 'bg-amber-100 dark:bg-amber-500/20',
    text: 'text-amber-700 dark:text-amber-400',
    icon: Warning,
    label: 'At Risk',
    bgRow: 'border-l-amber-400',
  },
  critical: {
    bg: 'bg-red-100 dark:bg-red-500/20',
    text: 'text-red-700 dark:text-red-400',
    icon: XCircle,
    label: 'Critical',
    bgRow: 'border-l-red-500',
  },
};

export function calculateMatterHealth(
  lastActivityDate?: string | number | null,
  nextHearingDate?: string | null,
  overdueTasks?: number,
): { level: HealthLevel; reason: string } {
  const now = new Date();
  let criticalFactors = 0;
  let attentionFactors = 0;
  let reason = '';

  // Check overdue tasks first - strongest signal
  if (overdueTasks !== undefined) {
    if (overdueTasks > 2) {
      criticalFactors++;
      reason = `${overdueTasks} overdue tasks`;
    } else if (overdueTasks > 0) {
      attentionFactors++;
      reason = `${overdueTasks} overdue task${overdueTasks > 1 ? 's' : ''}`;
    }
  }

  // Check next hearing urgency
  if (nextHearingDate) {
    const hearingDate = new Date(nextHearingDate);
    const daysUntil = Math.floor((hearingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) {
      // Past hearing without outcome
      criticalFactors++;
      reason = reason || 'Past hearing needs outcome';
    } else if (daysUntil < 3) {
      criticalFactors++;
      reason = reason || `Hearing in ${daysUntil === 0 ? 'today' : daysUntil === 1 ? 'tomorrow' : `${daysUntil} days`}`;
    } else if (daysUntil < 7) {
      attentionFactors++;
      reason = reason || `Hearing in ${daysUntil} days`;
    }
  }

  // Check last activity
  if (lastActivityDate) {
    const lastDate = new Date(lastActivityDate);
    const daysSince = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince > 14) {
      attentionFactors++;
      reason = reason || `Inactive for ${daysSince} days`;
    } else if (daysSince > 21) {
      criticalFactors++;
      reason = reason || `Stale: ${daysSince} days since activity`;
    }
  }

  if (criticalFactors > 0) {
    return { level: 'critical', reason };
  }
  if (attentionFactors > 0) {
    return { level: 'at_risk', reason };
  }
  return { level: 'healthy', reason: 'On track' };
}

export function MatterHealthBadge({
  lastActivityDate,
  nextHearingDate,
  overdueTasks,
  compact = false,
  showTooltip = true,
}: MatterHealthBadgeProps) {
  const { level, reason } = useMemo(
    () => calculateMatterHealth(lastActivityDate, nextHearingDate, overdueTasks),
    [lastActivityDate, nextHearingDate, overdueTasks],
  );

  const config = HEALTH_CONFIG[level];
  const Icon = config.icon;

  if (compact) {
    return (
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${config.bg}`}
        title={showTooltip ? `${config.label}: ${reason}` : undefined}
      >
        <Icon className={`h-3 w-3 ${config.text}`} weight="fill" />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${config.bg}`}
      title={showTooltip ? reason : undefined}
    >
      <Icon className={`h-3 w-3 ${config.text}`} weight="fill" />
      <span className={`text-[10px] font-semibold ${config.text}`}>{config.label}</span>
    </span>
  );
}

export function getMatterHealthRowClass(
  lastActivityDate?: string | number | null,
  nextHearingDate?: string | null,
  overdueTasks?: number,
): string {
  const { level } = calculateMatterHealth(lastActivityDate, nextHearingDate, overdueTasks);
  return HEALTH_CONFIG[level].bgRow;
}
